<template>
  <div v-if="layout === 'vertical'" class="book-card"
    :class="{'book-card--mobile': deviceType === 'mobile'}" @click="$emit('click')">
    <img :src="book.coverImage" :alt="book.title" class="book-card__cover">

    <div class="book-card__overlay">
      <div class="book-card__title">{{ book.title }}</div>
      <div class="book-card__author">{{ book.author }}</div>
    </div>

    <div class="book-card__tags">
      <div class="book-card__tag book-card__tag--category">{{ book.category }}</div>
      <div class="book-card__tag book-card__tag--rating">
        <img src="@/assets/star-w.svg" alt="star" class="book-card__tag-icon">
        <span>{{ book.rating }}</span>
      </div>
    </div>
  </div>
  <div v-else-if="layout === 'horizontal'" class="book-card-horizontal" :style="horizontalCardStyle"
    @click="$emit('click')">
    <div class="book-card-horizontal__overlay-gradient"></div>
    <div class="book-card-horizontal__content">
      <img :src="book.coverImage" :alt="book.title" class="book-card-horizontal__cover">
      <div class="book-card-horizontal__info">
        <div class="book-card-horizontal__info-top">
          <div class="book-card-horizontal__title">{{ book.title }}</div>
          <div class="book-card-horizontal__author">By {{ book.author }}</div>
        </div>
        <div class="book-card-horizontal__info-bottom">
          <div class="book-card-horizontal__tag">{{ book.category }}</div>
          <div class="book-card-horizontal__tag book-card-horizontal__tag--rating">
            <img src="@/assets/star-w.svg" alt="star">
            <span>{{ book.rating }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="layout === 'cover-hover'" class="book-card-cover-hover" @click="$emit('click')">
    <img :src="book.coverImage" :alt="book.title" class="book-card-cover-hover__cover" />
    <div class="book-card-cover-hover__overlay">
      <div class="book-card-cover-hover__overlay-content">
        <div class="book-card-cover-hover__info">
          <div class="book-card-cover-hover__title">{{ book.title }}</div>
          <div class="book-card-cover-hover__author">By {{ book.author }}</div>
        </div>
        <div class="book-card-cover-hover__tags">
          <div class="book-card-cover-hover__tag">{{ book.category }}</div>
          <div class="book-card-cover-hover__tag book-card-cover-hover__tag--rating">
            <img src="@/assets/star-w.svg" alt="star" />
            <span>{{ book.rating }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'BookCard',
  props: {
    book: {
      type: Object,
      required: true,
    },
    layout: {
      type: String,
      default: 'vertical', // 'vertical' or 'horizontal'
    },
  },
  computed: {
    ...mapGetters(['deviceType']),
    horizontalCardStyle () {
      if (this.layout === 'horizontal' && this.book.coverImage) {
        return {
          backgroundImage: `url(${this.book.coverImage})`,
        };
      }
      return {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.book-card {
	width: rem(252);
	height: rem(378);
	border-radius: rem(12);
	overflow: hidden;
	position: relative;
	cursor: pointer;
	box-shadow: 0px 4px 8px 0px rgba(22, 19, 80, 0.28);
	transition: transform 0.3s ease-in-out;

	&:hover {
		transform: translateY(-4px);
	}

	&__cover {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__overlay {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		padding: rem(24) rem(12) rem(12);
		background: linear-gradient(180deg, rgba(22, 19, 80, 0) 0%, rgba(22, 19, 80, 0.5) 20%, rgba(22, 19, 80, 1) 100%);
		color: #fff;
		display: flex;
		flex-direction: column;
		gap: rem(8);
	}

	&__title {
		font-size: rem(20);
		font-weight: 900;
		line-height: 1.2;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__author {
		font-size: rem(12);
		font-weight: 400;
		color: #fff;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__tags {
		position: absolute;
		top: 0;
		left: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}

	&__tag {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: rem(8);
		font-size: rem(13);
		font-weight: 700;
		color: #fff;
		border: 1px solid rgba(255, 255, 255, 0.1);

		&--category {
			background: #161350;
			padding: rem(6) rem(12);
			border-radius: rem(12) 0 rem(12) 0;
		}

		&--rating {
			background: #7f0026;
			padding: rem(6) rem(12);
			width: rem(64);
			height: rem(24);
			border-radius: 0 0 rem(12) 0;
			box-sizing: border-box;

			.book-card__tag-icon {
				width: rem(12);
				height: rem(12);
			}
		}
	}
}

// Styles for the new horizontal layout
.book-card-horizontal {
	position: relative;
	width: rem(344);
	height: rem(280);
	border-radius: rem(12);
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.3s ease;
	background-size: cover;
	background-position: center;

	&:hover {
		transform: translateY(-4px);
	}

	&__overlay-gradient {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(180deg, rgba(22, 19, 80, 0.9) 0%, rgba(22, 19, 80, 0.3) 100%);
		backdrop-filter: blur(6px);
		z-index: 1;
	}

	&__content {
		position: relative;
		z-index: 2;
		display: flex;
		align-items: center;
		height: 100%;
		padding: rem(20);
		box-sizing: border-box;
	}

	&__cover {
		width: rem(160);
		height: rem(240);
		border-radius: rem(12);
		object-fit: cover;
		box-shadow: 4px 4px 8px 0px rgba(22, 19, 80, 0.28);
		border: 1px solid rgba(255, 255, 255, 0.2);
		flex-shrink: 0;
	}

	&__info {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		height: rem(240);
		padding-left: rem(16);
		flex: 1;
		color: #fff;
		overflow: hidden;
	}

	&__info-top {
		display: flex;
		flex-direction: column;
		gap: rem(16);
	}

	&__title {
		font-size: rem(20);
		font-weight: 900;
		line-height: 1.2;
		max-height: calc(1.2em * 4); // Fallback for max-height
		display: -webkit-box;
		-webkit-line-clamp: 4;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__author {
		font-size: rem(12);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__info-bottom {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
	}

	&__tag {
		display: inline-flex;
		align-items: center;
		gap: rem(8);
		padding: rem(6) rem(12);
		border-radius: rem(8);
		font-size: rem(13);
		font-weight: 700;
		background: #161350;
		border: 1px solid rgba(255, 255, 255, 0.1);

		&--rating {
			background: #7f0026;
			img {
				width: rem(12);
				height: rem(12);
			}
		}
	}
}

.book-card-cover-hover {
	position: relative;
	width: rem(160);
	height: rem(240);
	border-radius: rem(12);
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.3s ease;
	box-shadow: 0px 4px 8px 0px rgba(22, 19, 80, 0.28);

	&:hover {
		transform: translateY(-4px);
		.book-card-cover-hover__overlay {
			opacity: 1;
		}
	}

	&__cover {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.3s ease;
	}

	&__overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		transition: opacity 0.3s ease;
		background: linear-gradient(180deg, rgba(22, 19, 80, 0.9) 0%, rgba(22, 19, 80, 0.3) 100%);
		backdrop-filter: blur(4px);
		color: #fff;
	}

	&__overlay-content {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: rem(20) rem(16);
		height: 100%;
		box-sizing: border-box;
	}

	&__info {
		display: flex;
		flex-direction: column;
		gap: rem(12);
	}

	&__title {
		font-size: rem(20);
		font-weight: 900;
		line-height: 1.2;
		display: -webkit-box;
		-webkit-line-clamp: 4;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__author {
		font-size: rem(12);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__tags {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
	}

	&__tag {
		display: inline-flex;
		align-items: center;
		gap: rem(8);
		padding: rem(6) rem(12);
		border-radius: rem(8);
		font-size: rem(13);
		font-weight: 700;
		border: 1px solid #fff;

		&--rating {
			img {
				width: rem(12);
				height: rem(12);
			}
		}
	}
}

.detect_deviece_mobile {
	.book-card-horizontal {
		width: rem(330);
		// background-image: none !important;

		&__overlay-gradient {
			background: linear-gradient(180deg, rgba(22, 19, 80, 0.9) 0%, rgba(22, 19, 80, 0.3) 100%);
			backdrop-filter: blur(rem(6));
		}
	}
}
</style> 